/**
 * Mobile off-canvas drawer: toggle open/closed, Esc-to-close, click-outside-
 * to-close, and a basic focus trap while open.
 *
 * New module, separate from air-light's inherited modules/navigation.js
 * (which manages the original #nav/#menu-items-wrapper inline mobile
 * panel). template-parts/header/offcanvas.php's toggle button reuses that
 * module's #nav-toggle id/class for the hamburger visuals and the shared
 * dropdown keyboard-nav modules' Escape handling (see the comment there),
 * but this file owns the open/close behaviour itself - navMobile() from
 * modules/navigation.js is intentionally not called in front-end.js, since
 * it targets #menu-items-wrapper (not present in this markup) and would
 * otherwise attach a second, conflicting click handler to the same button.
 *
 * Reuses body.js-nav-active (not a new class) so the existing hamburger-to-
 * "X" animation, the .site-header background swap and the mobile scroll
 * lock in navigation/_nav-toggle.scss, layout/_site-header.scss and
 * navigation/_nav-mobile.scss apply here for free.
 */
function initOffcanvas() {
  const toggle = document.getElementById('nav-toggle');
  const drawer = document.getElementById('offcanvas');

  if (!toggle || !drawer) {
    return;
  }

  const panel = drawer.querySelector('.offcanvas__panel');
  const closeButton = drawer.querySelector('.offcanvas__close');

  function getFocusableElements() {
    return [
      ...drawer.querySelectorAll(
        'a, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])',
      ),
    ]
      .filter((el) => !el.hasAttribute('disabled'))
      .filter((el) => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length));
  }

  function isOpen() {
    return document.body.classList.contains('js-nav-active');
  }

  // Escape must be intercepted before it reaches air-light's shared
  // dropdown keyboard-nav modules (modules/navigation/a11y-dropdown-menu-
  // keyboard-navigation(-click).js): those close "main-menu" dropdowns on
  // Escape assuming a .sub-menu always exists in scope, which isn't
  // guaranteed for every focusable link. Capturing here and stopping
  // propagation keeps Escape scoped to just closing this drawer while it's
  // open (focus never leaves the drawer while it's open, see the Tab trap
  // below, so any Escape received here did originate inside it).
  function onKeydown(e) {
    if (e.key === 'Escape' || e.keyCode === 27) {
      e.preventDefault();
      e.stopPropagation();
      close();
      return;
    }

    if (e.key !== 'Tab') {
      return;
    }

    const focusable = getFocusableElements();

    if (!focusable.length) {
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function onClickOutside(e) {
    if (!panel.contains(e.target) && e.target !== toggle && !toggle.contains(e.target)) {
      close();
    }
  }

  function open() {
    document.body.classList.add('js-nav-active');
    drawer.setAttribute('aria-hidden', 'false');
    toggle.setAttribute('aria-expanded', 'true');

    const focusable = getFocusableElements();
    if (focusable.length) {
      focusable[0].focus();
    }

    // Capture phase: run before the click/keydown reaches its target, so
    // Escape can be stopped before the shared dropdown modules see it.
    document.addEventListener('keydown', onKeydown, true);
    document.addEventListener('click', onClickOutside, true);
  }

  function close() {
    document.body.classList.remove('js-nav-active');
    drawer.setAttribute('aria-hidden', 'true');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.focus();

    document.removeEventListener('keydown', onKeydown, true);
    document.removeEventListener('click', onClickOutside, true);
  }

  toggle.addEventListener('click', () => {
    if (isOpen()) {
      close();
    } else {
      open();
    }
  });

  if (closeButton) {
    closeButton.addEventListener('click', close);
  }
}

export default initOffcanvas;
